import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

interface CloneMeetingParams {
	sourceMeetingId: string;
	userId: string;
	timeZone?: string;
}

interface UseSchedulerConfigCloneMutationOptions {
	onSuccess?: (response: Api.IMeetingConfigResponse) => void;
	onError?: (err: Api.IOperationResultNoValue) => void;
}

/**
 * Creates a mutation function for cloning meeting configurations.
 *
 * @param sourceMeetingId - The ID of the meeting configuration to clone
 * @param userId - The ID of the user who will own the cloned meeting
 * @param timeZone - Optional timezone for the cloned meeting (defaults to browser timezone)
 * @returns Promise resolving to the cloned meeting configuration response
 */
const createCloneMeetingMutation =
	(userSession: Api.UserSessionContext) =>
	({ sourceMeetingId, userId, timeZone }: CloneMeetingParams) => {
		const queryParams: { forUserId: string; timeZone?: string } = {
			forUserId: userId,
		};

		if (timeZone) {
			queryParams.timeZone = timeZone;
		}

		return userSession.webServiceHelper.callAsync<Api.IMeetingConfigResponse>(
			Api.ImpersonationBroker.composeApiUrl({
				queryParams,
				urlPath: `scheduler/config/clone/${encodeURIComponent(sourceMeetingId)}`,
			}),
			'POST'
		);
	};

/**
 * Hook for cloning meeting configurations using the backend API.
 *
 * Creates a clone of an existing meeting configuration with a new ID and shortCode.
 * The cloned meeting is created in an unpublished state and can be edited before saving.
 * Optionally accepts a timezone parameter for timezone-specific meeting scheduling.
 *
 * @param options - Configuration options for the mutation
 * @param options.onSuccess - Callback executed when cloning succeeds
 * @param options.onError - Callback executed when cloning fails
 * @returns React Query mutation object for cloning meeting configurations
 *
 * @example
 * ```typescript
 * const cloneMutation = useSchedulerConfigCloneMutation({
 *   onSuccess: (clonedMeeting) => {
 *     console.log('Meeting cloned:', clonedMeeting.id);
 *   },
 *   onError: (error) => {
 *     console.error('Clone failed:', error);
 *   }
 * });
 *
 * // Clone a meeting
 * cloneMutation.mutate({
 *   sourceMeetingId: 'original-meeting-id',
 *   userId: 'user-id',
 *   timeZone: 'America/New_York' // optional
 * });
 * ```
 */
export function useSchedulerConfigCloneMutation(options: UseSchedulerConfigCloneMutationOptions = {}) {
	const userSession = useUserSession();
	const { onSuccess, onError } = options;

	return useMutation({
		mutationFn: createCloneMeetingMutation(userSession),
		onError,
		onSuccess,
	});
}
